import {
  ANYWHERE_PLACE_ID,
  LOCATION_SEARCH_INPUT_PLACEHOLDER_LABEL,
  LOCATION_SEARCH_INPUT_TITLE_LABEL,
} from 'constants/search'
import useToggle from 'hooks/useToggle'
import { EmptyArray, take, without } from 'lib/array/arrayUtils'
import noop from 'lib/function/noop'
import React, { ComponentProps, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import SearchPlaceSelectDropdown from './SearchPlaceSelectDropdown'
import SearchPlaceSelectInline from './SearchPlaceSelectInline'
import HotelSearchPlaceSelectDropdown from '../Hotels/HotelSearchPlaceSelectDropdown'
import TourSearchPlaceSelectDropdown from '../TourV2/TourSearchPlaceSelectDropdown'
import { TypeaheadActionType } from 'analytics/snowplow/events'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { GlobalSearchDispatchContext } from 'contexts/GlobalSearch/GlobalSearchContexts'

const components: Record<'dropdown' | 'inline', Utils.PartialRecord<App.SearchPlaceVertical | 'default', any>> = {
  // desktop
  dropdown: {
    default: SearchPlaceSelectDropdown,
    hotel: HotelSearchPlaceSelectDropdown,
    tour: TourSearchPlaceSelectDropdown,
  },
  // mobile
  inline: {
    default: SearchPlaceSelectInline,
  },
}

interface Props {
  open?: boolean;
  className?: string;
  label?: string;
  value?: App.SearchItem;
  placeholder?: string;
  searchTypes?: Array<App.SearchType>;
  placeTypes?: Array<App.SearchPlaceType>;
  searchVertical?: App.SearchPlaceVertical;
  /** Items to show when a user hasn't entered any search */
  placeholderItems?: Array<App.SearchItem>;
  placeholderTitle?: string;
  /** Full list is used for keyword matching. The displayed subset is rendered before the user types. */
  businessOfficeItems?: Array<App.SearchItem>;
  displayedBusinessOfficeItems?: Array<App.SearchItem>;
  businessOfficeTitle?: string;
  /** When the selected item changes */
  onChange?: (
    item?: App.SearchItem,
    searchPhase?: string,
    actionType?: TypeaheadActionType,
    searchItemPosition?: App.SearchItemPosition
  ) => void;
  onToggle?: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  onResultsChange?: (results: Array<App.SearchItem>, searchPhrase: string, numColumns?: number) => void;
  inputRef?: React.Ref<HTMLInputElement | null>;
  controlsContainerRef?: React.RefObject<HTMLDivElement | null>;
  required?: boolean;
  /** A list of recent search items, will be displayed along with placeholder items */
  recentSearchItems?: Array<App.FullRecentSearch>;
  /** Use this to supply your own search results list the input will use */
  overrideSearchItems?: Array<App.SearchItem>;
  variant: 'inline' | 'dropdown';
  dropdownSize?: ComponentProps<typeof SearchPlaceSelectDropdown>['size']
  /**
   * Dismiss the dropdown once an item is selected
   *
   * @default true
   */
  closeOnChange?: boolean;
  onClear?: () => void;
  onPhraseChange?: (searchPhrase: string) => void
}

const SELECTABLE_RESULT_TYPES: Array<App.SearchType> = ['destination', 'property', 'landmark', 'airport', 'departurePort', 'lat_long']

/**
 * @todo NEEDS OVERHAUL
 */
function SearchPlaceSelect(props: Props) {
  const {
    open,
    placeholder = LOCATION_SEARCH_INPUT_PLACEHOLDER_LABEL,
    label = LOCATION_SEARCH_INPUT_TITLE_LABEL,
    value,
    placeholderItems = EmptyArray,
    businessOfficeItems = EmptyArray,
    displayedBusinessOfficeItems = EmptyArray,
    onChange = noop,
    onResultsChange = noop,
    inputRef,
    recentSearchItems = EmptyArray,
    closeOnChange = true,
    onToggle,
    variant,
    dropdownSize,
    controlsContainerRef,
    searchVertical,
    onClear = noop,
    onPhraseChange = noop,
  } = props

  const [localRecentSearches, setLocalRecentSearches] = useState<Array<App.FullRecentSearch>>(recentSearchItems)
  const [searchPhrase, setSearchPhrase] = useState<string>(value?.format.mainText ?? '')
  const [selectedDropdown, setSelectedDropdown] = useState<number | undefined>()
  const localInputRef = useRef<HTMLInputElement>(null)
  const [localOpen, , openDropdown, closeDropdown] = useToggle()
  const [results, setResults] = useState<Array<App.SearchItem>>([])
  const finalOpen = !!(open ?? localOpen)

  useEffect(() => {
    onPhraseChange(searchPhrase)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchPhrase])

  useEffect(() => {
    setLocalRecentSearches(recentSearchItems)
  }, [recentSearchItems])

  useEffect(() => {
    if (value?.format.mainText) {
      // update search phrase if search item updated
      setSearchPhrase(value.format.mainText)
    }
  }, [value])

  useEffect(() => {
    if (searchPhrase === '') {
      onClear()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchPhrase])

  const handleOpen = useCallback((e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    openDropdown()
    if (onToggle) {
      onToggle(e)
    }
  }, [onToggle, openDropdown])

  useImperativeHandle(inputRef, () => localInputRef.current!)
  const onLocationSelect = useCallback((
    searchItem?: App.SearchItem,
    mouseEvent?: React.MouseEvent<HTMLElement>,
    actionType?: TypeaheadActionType,
    searchItemPosition?: App.SearchItemPosition,
  ) => {
    setSearchPhrase(searchItem?.format.mainText ?? '')
    onChange(searchItem, searchPhrase, actionType, searchItemPosition)
    if (closeOnChange) {
      closeDropdown()
    }
  }, [onChange, searchPhrase, closeOnChange, closeDropdown])

  const handleResultsChange = useCallback((results: Array<App.SearchItem>, searchPhrase: string, numOfColumns: number, numOfRows: number) => {
    setResults(results)
    onResultsChange(results, searchPhrase, numOfColumns, numOfRows)
  }, [onResultsChange])

  const showPlaceholder = (searchPhrase.length === 0 && (placeholderItems.length > 0 || businessOfficeItems.length > 0)) || !!(value && value.format.mainText === searchPhrase)
  const hasRecentSearchItems = !!localRecentSearches.length

  const [recentSearches, placeholdersSearches] = useMemo(() => {
    const filteredPlaceholderItems = placeholderItems.filter(item => item.value !== ANYWHERE_PLACE_ID)
    const uniqueSearchItems = take(localRecentSearches, 3)

    let maxItems = 7

    if (searchVertical === 'hotel') {
      maxItems = 12
    }

    return [
      uniqueSearchItems,
      take(filteredPlaceholderItems, maxItems - uniqueSearchItems.length),
    ]
  }, [localRecentSearches, placeholderItems, searchVertical])

  const onRecentSearchDelete = useCallback((item: App.FullRecentSearch) => {
    setLocalRecentSearches(without(localRecentSearches, item))
  }, [localRecentSearches])

  const onSearchPhraseChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchPhrase(e.currentTarget.value)
    // clear the previous selected item during typing
    onChange(undefined)
  }, [onChange])

  const onClearClick = useCallback(() => {
    if (localInputRef.current) {
      localInputRef.current.value = ''
    }
    // clear the search phrase, selected item and suggested items
    setSearchPhrase('')
    onChange(undefined)
    onResultsChange([])
  }, [onChange, onResultsChange])

  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)
  const onRecentSearchClick = useCallback((recentSearch: App.FullRecentSearch) => {
    globalSearchDispatch({
      type: GlobalSearchStateActions.SET_SEARCH_ITEM,
      searchItem: recentSearch.searchItem,
    })
    closeDropdown()
  }, [closeDropdown, globalSearchDispatch])

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    const selectableResults = results.filter(result => SELECTABLE_RESULT_TYPES.includes(result.searchType))
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (selectedDropdown === 0) {
        return
      }
      setSelectedDropdown(prev => prev ? prev - 1 : undefined)
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (selectedDropdown === selectableResults.length - 1) {
        return
      }
      setSelectedDropdown(prev => prev === undefined ? 0 : prev + 1)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (selectedDropdown === undefined) {
        if (!showPlaceholder && selectableResults.length > 0) {
          onLocationSelect(selectableResults[0], undefined, 'default', { x: 0, y: 0 })
        }
        return
      } else {
        if (showPlaceholder && selectedDropdown < recentSearches.length) {
          onRecentSearchClick(recentSearches[selectedDropdown])
        } else if (showPlaceholder && selectedDropdown < recentSearches.length + displayedBusinessOfficeItems.length) {
          onLocationSelect(displayedBusinessOfficeItems[selectedDropdown - recentSearches.length])
        } else if (showPlaceholder && selectedDropdown > recentSearches.length + displayedBusinessOfficeItems.length - 1) {
          onLocationSelect(placeholdersSearches[selectedDropdown - recentSearches.length - displayedBusinessOfficeItems.length])
        } else if (!showPlaceholder) {
          if (selectedDropdown < selectableResults.length) {
            onLocationSelect(selectableResults[selectedDropdown])
          }
        }
      }
      setSelectedDropdown(undefined)
    } else {
      if (!finalOpen) {
        // if typing happens when the dropdown closed, open it
        handleOpen()
      }
    }
  }, [onLocationSelect, onRecentSearchClick, placeholdersSearches, displayedBusinessOfficeItems, recentSearches, results, selectedDropdown, showPlaceholder, finalOpen, handleOpen])

  const SearchPlaceSelectProps = {
    ...props,
    onKeyDown,
    localInputRef,
    controlsContainerRef,
    searchPhrase,
    onSearchPhraseChange,
    handleOpen,
    onClearClick,
    finalOpen,
    closeDropdown,
    hasRecentSearchItems,
    recentSearches,
    selectedDropdown,
    onRecentSearchDelete,
    onRecentSearchClick,
    placeholdersSearches,
    onLocationSelect,
    showPlaceholder,
    handleResultsChange,
    label,
    placeholder,
    placeholderItems,
    setSearchPhrase,
  }

  const Component = components[variant][searchVertical ?? 'default'] ?? components[variant].default

  return <Component {...SearchPlaceSelectProps} {...(variant === 'dropdown' && { size: dropdownSize })} />
}

export default SearchPlaceSelect
